import type { VerificationReport, AgentActionReceipt } from "@sequesign/lib/types";
import {
  FAILURE_REASON_EXPLAIN,
  summaryState,
  summaryStateLabel,
  witnessedBadge,
  levelBadge
} from "../copy.js";

interface SummaryProps {
  report: VerificationReport;
  receipt: AgentActionReceipt | null;
  sourceLabel: string;
  actionCount: number;
}

const RECEIPT_MODE_LABELS: Record<string, string> = {
  freeform: "Freeform",
  schema_validated: "Schema validated",
  profile_constrained: "Profile constrained"
};

export function Summary({ report, receipt, sourceLabel, actionCount }: SummaryProps) {
  const state = summaryState(report);
  const stateLabel = summaryStateLabel(state);
  const witnessed = witnessedBadge(report.flags.witnessed);
  const level = levelBadge(report.verification_level);
  const mode = receipt?.receipt_mode ?? report.receipt_mode ?? "freeform";
  const modeLabel = RECEIPT_MODE_LABELS[mode] ?? mode;
  const chainId = receipt?.chain.chain_id ?? report.chain?.chain_id ?? "unknown";

  return (
    <div className={`summary ${state}`}>
      <h2 className="summary-state">{stateLabel}</h2>

      {/* Witnessed and verification level as two independent badges, read
          directly from the report (the verifier ran, so no inference).
          Per protocol-spec.md 6.3 they are distinct claims and MUST NOT be
          collapsed into one ordinal. When the level is L1_WITNESSED both
          read "Witnessed" / "L1 Witnessed"; that repetition is intentional. */}
      <div className="summary-badges">
        <span className={`badge ${witnessed.variant}`}>{witnessed.label}</span>
        {/* Dedicated "level" class (not the shared "neutral" used by "Not
            witnessed") so the level badge can read as present-but-neutral
            without restyling the witnessed axis. */}
        <span className="badge level">{level.label}</span>
        {/* Agent identity is its own axis (registered vs self-asserted),
            orthogonal to the level: a receipt can be L2_KEY_BOUND while the
            agent identity is only self-asserted. Render it so a registered
            (platform-vouched) identity is visibly distinct from a
            self-asserted key. */}
        {report.identity_assurance ? (
          <span className="badge level">
            {report.identity_assurance === "registered"
              ? "Registered identity"
              : "Self-asserted identity"}
          </span>
        ) : null}
        {/* Template authenticity (Phase 5): who vouches for the mandate rules,
            an axis distinct from the level and the agent identity. Present only
            for a parameterized (V1) profile-constrained receipt; rendered so an
            attested template is visibly distinct from an unrecognized/unattested
            one. */}
        {report.template_authenticity ? (
          <span className="badge level">
            {report.template_authenticity === "attested"
              ? "Attested template"
              : report.template_authenticity === "unrecognized"
                ? "Unrecognized template author"
                : "Unattested template"}
          </span>
        ) : null}
      </div>

      <div className="summary-grid">
        <div className="summary-cell">
          <span className="summary-label">Receipt mode</span>
          <span className="summary-value-plain">{modeLabel}</span>
        </div>
        <div className="summary-cell">
          <span className="summary-label">Chain identifier</span>
          <span className="summary-value">{chainId}</span>
        </div>
        <div className="summary-cell">
          <span className="summary-label">Actions in chain</span>
          <span className="summary-value-plain">{actionCount}</span>
        </div>
        <div className="summary-cell">
          <span className="summary-label">Source</span>
          <span className="summary-value-plain">{sourceLabel}</span>
        </div>
        {/* Agent identity assurance: whether the signing key is a
            platform-vouched registered identity or self-asserted. Distinct
            from the level (key-binding) and the witness axis. */}
        {report.identity_assurance ? (
          <div className="summary-cell">
            <span className="summary-label">Agent identity</span>
            <span className="summary-value-plain">
              {report.identity_assurance === "registered"
                ? "Registered (vouched)"
                : "Self-asserted (unregistered)"}
            </span>
          </div>
        ) : null}
        {/* Template authenticity (Phase 5): the grade plus, when attested, the
            vouching author id. Only present for a parameterized (V1)
            profile-constrained receipt; freeform / V0 omit it. */}
        {report.template_authenticity ? (
          <div className="summary-cell">
            <span className="summary-label">Template authenticity</span>
            <span className="summary-value-plain">
              {report.template_authenticity === "attested"
                ? `Attested by ${report.template_author?.author_id ?? "author"}`
                : report.template_authenticity === "unrecognized"
                  ? "Signed by an unrecognized author"
                  : "Not author-signed"}
            </span>
          </div>
        ) : null}
        {/* Trust anchor visibility. The hosted verifier anchors to the
            witness's well-known keys (trust_anchor_mode "external"), so a
            third-party witnessed check shows the matched witness key. A
            receipt with no witness attestations reports "none" (integrity
            only). This makes the trust decision explicit, not implicit. */}
        {report.trust_anchor_mode ? (
          <div className="summary-cell">
            <span className="summary-label">Witness trust anchor</span>
            <span className="summary-value">
              {report.trust_anchor_mode === "external" && report.flags.witnessed
                ? `Witnessed by ${report.witness_trust_anchor?.matched_key_id ?? "unknown"}`
                : report.trust_anchor_mode === "self" && report.flags.witnessed
                  ? `Integrity self-check against ${report.witness_trust_anchor?.matched_key_id ?? "unknown"}`
                  : report.trust_anchor_mode === "none"
                    ? "No witness attestations (integrity only)"
                    : "Not anchored to a trusted witness key"}
            </span>
          </div>
        ) : null}
      </div>

      {!report.valid && report.reason ? (
        <div className="failure-reason">
          <p className="failure-reason-title">Failure reason</p>
          <p className="failure-reason-code">{report.reason}</p>
          <p className="failure-reason-explanation">
            {FAILURE_REASON_EXPLAIN[report.reason] ??
              "The verifier rejected the receipt for this reason. The flag rows below indicate which checks passed and which failed."}
          </p>
          {report.action ? (
            <p className="failure-reason-explanation">
              First detected at action sequence {report.action.sequence} (
              {report.action.action_type}).
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
